import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, map } from "rxjs";
import { environment } from "src/environments/environment";
import {
  DemoFormViewModel,
  DomainViewModel,
  FilterViewModel,
  FunctionalityViewModel,
  GroupViewModel,
  IndustryViewModel,
  ProcessViewModel,
  AccountViewModel,
  UseCaseFormViewModel,
  UseCaseViewModel,
  VmImage
} from "../core/model/ContentManagement";
import { BrdLoggerService } from "@board/cloud-library/components/services";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root"
})
export class MetadataService {
  controllerName = "Metadata";
  private apiUrl: string = `${environment.API}/${this.controllerName}`;

  private functionalitiesSubject = new BehaviorSubject<FunctionalityViewModel[]>([]);
  functionalities$ = this.functionalitiesSubject.asObservable();

  private functionFilterSubject = new BehaviorSubject<number[]>(undefined);
  functionFilter$ = this.functionFilterSubject.asObservable();

  private groupsSubject = new BehaviorSubject<GroupViewModel[]>([]);
  groups$ = this.groupsSubject.asObservable();

  private useCasesSubject = new BehaviorSubject<UseCaseViewModel[]>([]);
  useCases$ = this.useCasesSubject.asObservable();

  private useCasesFilterSubject = new BehaviorSubject<number[]>(undefined);
  filterUseCases$ = this.useCasesFilterSubject.asObservable();

  private processesSubject = new BehaviorSubject<ProcessViewModel[]>([]);
  processes$ = this.processesSubject.asObservable();

  private domainSubject = new BehaviorSubject<DomainViewModel[]>([]);
  domains$ = this.domainSubject.asObservable();

  private industrySubject = new BehaviorSubject<IndustryViewModel[]>([]);
  industries$ = this.industrySubject.asObservable();

  private latestVersionSubject = new BehaviorSubject<VmImage>(null);
  latestVersion$ = this.latestVersionSubject.asObservable();

  private accountsSubject = new BehaviorSubject<AccountViewModel[]>([]);
  accounts$ = this.accountsSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private loggerService: BrdLoggerService
  ) {}

  getFunctinalities(): Observable<FunctionalityViewModel[]> {
    return this.httpClient.get<FunctionalityViewModel[]>(`${this.apiUrl}/functionality`);
  }

  getFormViewModel(): Observable<DemoFormViewModel> {
    return this.httpClient.get<DemoFormViewModel>(`${this.apiUrl}/form`);
  }

  getFilterViewModel(): Observable<FilterViewModel> {
    return this.httpClient.get<FilterViewModel>(`${this.apiUrl}/filter`);
  }

  setFunctinionalities(value: FunctionalityViewModel[]): void {
    this.functionalitiesSubject.next(value);
  }

  setFilterFunctionalities(value: number[]): void {
    this.functionFilterSubject.next(value);
  }

  setUseCases(value: UseCaseViewModel[]): void {
    this.useCasesSubject.next(value);
  }

  setFilterUseCases(value: number[]): void {
    this.useCasesFilterSubject.next(value);
  }

  setDomains(value: DomainViewModel[]): void {
    this.domainSubject.next(value);
  }

  setProcesses(value: ProcessViewModel[]): void {
    this.processesSubject.next(value);
  }

  setGroups(value: GroupViewModel[]): void {
    this.groupsSubject.next(value);
  }

  setIndustries(value: IndustryViewModel[]): void {
    this.industrySubject.next(value);
  }

  setAccounts(value: AccountViewModel[]): void {
    this.accountsSubject.next(value);
  }

  addFunctionality(value: FunctionalityViewModel): Observable<FunctionalityViewModel> {
    return this.httpClient.post<FunctionalityViewModel>(`${this.apiUrl}/functionality`, value).pipe(
      map(res => {
        const currentFuncs = [...this.functionalitiesSubject.value];
        this.updateListValues(res, currentFuncs);
        this.functionalitiesSubject.next(currentFuncs);

        const currentGroups = [...this.groupsSubject.value];
        res.groups.forEach(group => {
          this.updateListValues(group, currentGroups);
        });
        this.groupsSubject.next(currentGroups);
        return res;
      })
    );
  }

  deleteFunctionality(ids: number[]): Observable<FunctionalityViewModel[]> {
    return this.httpClient.post<FunctionalityViewModel[]>(`${this.apiUrl}/functionality/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentFuncs = [...this.functionalitiesSubject.value];
        currentFuncs = currentFuncs.filter(x => !deletedIds.includes(x.id));
        this.functionalitiesSubject.next(currentFuncs);

        return res;
      })
    );
  }

  addUseCase(value: UseCaseFormViewModel): Observable<UseCaseViewModel> {
    return this.httpClient.post<UseCaseViewModel>(`${this.apiUrl}/usecase`, value).pipe(
      map(res => {
        const currentUsecases = [...this.useCasesSubject.value];
        this.updateListValues(res, currentUsecases);
        this.useCasesSubject.next(currentUsecases);

        const currentDomains = [...this.domainSubject.value];
        this.updateListValues({ id: res.domainId, name: value.domainName }, currentDomains);
        this.domainSubject.next(currentDomains);

        const currentProcesses = [...this.processesSubject.value];
        this.updateListValues({ id: res.processId, domainId: res.domainId, name: value.processName }, currentProcesses);
        this.processesSubject.next(currentProcesses);

        return res;
      })
    );
  }

  deleteUseCases(ids: number[]): Observable<UseCaseViewModel[]> {
    return this.httpClient.post<UseCaseViewModel[]>(`${this.apiUrl}/usecase/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentUsecases = [...this.useCasesSubject.value];
        currentUsecases = currentUsecases.filter(x => !deletedIds.includes(x.id));
        this.useCasesSubject.next(currentUsecases);

        return res;
      })
    );
  }

  addDomain(value: DomainViewModel): Observable<DomainViewModel> {
    return this.httpClient.post<DomainViewModel>(`${this.apiUrl}/domain`, value).pipe(
      map(res => {
        const currentDomains = [...this.domainSubject.value];
        this.updateListValues(res, currentDomains);
        this.domainSubject.next(currentDomains);

        return res;
      })
    );
  }

  deleteDomains(ids: number[]): Observable<DomainViewModel[]> {
    return this.httpClient.post<DomainViewModel[]>(`${this.apiUrl}/domain/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentDomains = [...this.domainSubject.value];
        currentDomains = currentDomains.filter(x => !deletedIds.includes(x.id));
        this.domainSubject.next(currentDomains);

        let currentProcesses = [...this.processesSubject.value];
        currentProcesses = currentProcesses.filter(x => !deletedIds.includes(x.domainId));
        this.processesSubject.next(currentProcesses);

        let currentUsecases = [...this.useCasesSubject.value];
        currentUsecases = currentUsecases.filter(x => !deletedIds.includes(x.domainId));
        this.useCasesSubject.next(currentUsecases);

        return res;
      })
    );
  }

  addProcess(value: ProcessViewModel): Observable<ProcessViewModel> {
    return this.httpClient.post<ProcessViewModel>(`${this.apiUrl}/process`, value).pipe(
      map(res => {
        const currentProcesses = [...this.processesSubject.value];
        this.updateListValues(res, currentProcesses);
        this.processesSubject.next(currentProcesses);

        return res;
      })
    );
  }

  deleteProcesses(ids: number[]): Observable<ProcessViewModel[]> {
    return this.httpClient.post<ProcessViewModel[]>(`${this.apiUrl}/process/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentProcesses = [...this.processesSubject.value];
        currentProcesses = currentProcesses.filter(x => !deletedIds.includes(x.id));
        this.processesSubject.next(currentProcesses);

        let currentUsecases = [...this.useCasesSubject.value];
        currentUsecases = currentUsecases.filter(x => !deletedIds.includes(x.processId));
        this.useCasesSubject.next(currentUsecases);

        return res;
      })
    );
  }

  addIndustry(value: IndustryViewModel): Observable<IndustryViewModel> {
    return this.httpClient.post<IndustryViewModel>(`${this.apiUrl}/industry`, value).pipe(
      map(res => {
        this.updateIndustries([res]);

        return res;
      })
    );
  }

  deleteIndustry(ids: number[]): Observable<IndustryViewModel[]> {
    return this.httpClient.post<IndustryViewModel[]>(`${this.apiUrl}/industry/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentIndustries = [...this.industrySubject.value];
        currentIndustries = currentIndustries.filter(x => !deletedIds.includes(x.id));
        this.industrySubject.next(currentIndustries);

        return res;
      })
    );
  }

  addGroup(value: GroupViewModel): Observable<GroupViewModel> {
    return this.httpClient.post<GroupViewModel>(`${this.apiUrl}/group`, value).pipe(
      map(res => {
        const currentGroups = [...this.groupsSubject.value];
        this.updateListValues(res, currentGroups);
        this.groupsSubject.next(currentGroups);

        return res;
      })
    );
  }

  deleteGroup(ids: number[]): Observable<GroupViewModel[]> {
    return this.httpClient.post<GroupViewModel[]>(`${this.apiUrl}/group/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentGroups = [...this.groupsSubject.value];
        currentGroups = currentGroups.filter(x => !deletedIds.includes(x.id));
        this.groupsSubject.next(currentGroups);

        return res;
      })
    );
  }

  updateIndustries(industries: IndustryViewModel[]): void {
    const currentIndustries = [...this.industrySubject.value];
    industries.forEach(indsutry => {
      this.updateListValues(indsutry, currentIndustries);
    });
    this.industrySubject.next(currentIndustries);
  }

  getLatestVersion(): Observable<VmImage> {
    return this.httpClient.get<VmImage>(`${this.apiUrl}/latestversion`).pipe(
      map(res => {
        this.latestVersionSubject.next(res);
        return res;
      })
    );
  }

  private updateListValues<T extends { id: number }>(newValue: T, values: T[]): void {
    const fIndex = values.findIndex(f => f.id === newValue.id);
    if (fIndex > -1) {
      values[fIndex] = newValue;
    } else {
      values.push(newValue);
    }
  }
}
